/**
 * Navigation Configuration for MDM UI
 * Defines all navigation items with permissions and icons
 *
 * ARCHITECTURAL STANDARD: Centralized navigation configuration
 * All navigation items should be defined here, not scattered across components
 */

import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import MedicationIcon from '@mui/icons-material/Medication';
import ComputerIcon from '@mui/icons-material/Computer';
import GroupIcon from '@mui/icons-material/Group';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import FeedbackIcon from '@mui/icons-material/Feedback';

/**
 * Navigation Item Structure:
 * {
 *   id: string (unique identifier)
 *   label: string (display text)
 *   path: string (route path)
 *   icon: Component (MUI icon component)
 *   permissions: string[] (required user roles/permissions)
 *   children?: array (sub-menu items)
 * }
 */

export const NAV_CONFIG = [
  {
    id: 'search',
    label: 'Search',
    icon: SearchIcon,
    permissions: ['user', 'admin'],
    children: [
      {
        id: 'hcp-search',
        label: 'HCP Search',
        path: '/search/hcp',
        icon: PersonIcon,
        permissions: ['user', 'admin'],
        relatedPaths: ['/details/hcp'],
      },
      {
        id: 'drug-search',
        label: 'Drug Search',
        path: '/search/drug',
        icon: MedicationIcon,
        permissions: ['user', 'admin'],
      },
    ],
  },
  {
    id: 'inbox',
    label: 'Inbox',
    icon: InboxIcon,
    permissions: ['user', 'admin'],
    children: [
      {
        id: 'inbox-user',
        label: 'DCR Inbox',
        path: '/inbox/user',
        icon: GroupIcon,
        permissions: ['user', 'admin'],
        relatedPaths: ['/hcp-dcr'],
      },
      {
        id: 'inbox-system',
        label: 'System Inbox',
        path: '/inbox/system',
        icon: ComputerIcon,
        permissions: ['user', 'admin'],
        relatedPaths: ['/hcp-new-request', '/hcp-merge'],
      },
      {
        id: 'inbox-drug',
        label: 'Drug Inbox',
        path: '/inbox/drug',
        icon: MedicationIcon,
        permissions: ['user', 'admin'],
      },
    ],
  },
  {
    id: 'market-baskets',
    label: 'Market Baskets',
    path: '/market-baskets',
    icon: ShoppingBasketIcon,
    permissions: ['user', 'admin'],
  },
  {
    id: 'history',
    label: 'History',
    icon: HistoryIcon,
    permissions: ['user', 'admin'],
    children: [
      {
        id: 'history-user',
        label: 'DCR History',
        path: '/history/user',
        icon: GroupIcon,
        permissions: ['user', 'admin'],
        relatedPaths: ['/history/request-details'],
      },
      {
        id: 'history-system',
        label: 'System History',
        path: '/history/system',
        icon: ComputerIcon,
        permissions: ['user', 'admin'],
      },
      {
        id: 'history-drug',
        label: 'Drug History',
        path: '/history/drug',
        icon: MedicationIcon,
        permissions: ['user', 'admin'],
      },
    ],
  },
  {
    id: 'configuration',
    label: 'Configuration',
    icon: SettingsIcon,
    permissions: ['admin'],
    children: [
      {
        id: 'configuration-hcp',
        label: 'HCP Config',
        path: '/configuration/hcp/model',
        icon: PersonIcon,
        permissions: ['admin'],
        relatedPaths: ['/configuration/hcp'],
      },
      {
        id: 'configuration-drug',
        label: 'Drug Config',
        path: '/configuration/drug/model',
        icon: MedicationIcon,
        permissions: ['admin'],
        relatedPaths: ['/configuration/drug'],
      },
    ],
  },
];

/**
 * Filter navigation items based on user permissions
 * @param {Array} navItems - Navigation configuration
 * @param {Array} userPermissions - User's permissions
 * @returns {Array} Filtered navigation items
 */
export const filterNavByPermissions = (navItems, userPermissions = []) => {
  if (!userPermissions || userPermissions.length === 0) {
    return [];
  }

  return navItems
    .filter((item) => {
      // Check if user has any of the required permissions
      return item.permissions.some((permission) => userPermissions.includes(permission));
    })
    .map((item) => {
      // If item has children, filter them too
      if (item.children) {
        return {
          ...item,
          children: filterNavByPermissions(item.children, userPermissions),
        };
      }
      return item;
    });
};

/**
 * Get navigation item by path
 * @param {string} path - Route path
 * @returns {Object|null} Navigation item
 */
export const getNavItemByPath = (path) => {
  for (const item of NAV_CONFIG) {
    if (item.path === path) {
      return item;
    }
    if (item.children) {
      const child = item.children.find((c) => c.path === path);
      if (child) {
        return child;
      }
    }
  }
  return null;
};

/**
 * Get screen name by path, considering related paths for active states.
 * @param {string} path - Route path
 * @returns {string|null} Screen label
 */
export const getScreenNameByPath = (path) => {
  const normalizePath = (p = '') => {
    if (!p) return '';
    if (p === '/') return '/';
    return p.replace(/\\/g, '/').replace(/\/+$/, '');
  };
  const currentPath = normalizePath(path);

  for (const item of NAV_CONFIG) {
    const itemPath = normalizePath(item.path);
    if (itemPath && (currentPath === itemPath || currentPath.startsWith(`${itemPath}/`))) {
      return item.label;
    }
    const itemRelatedMatch = item.relatedPaths?.some((pattern) => {
      const normalizedPattern = normalizePath(pattern);
      return (
        normalizedPattern &&
        (currentPath === normalizedPattern || currentPath.startsWith(`${normalizedPattern}/`))
      );
    });
    if (itemRelatedMatch) return item.label;

    if (item.children) {
      const child = item.children.find((c) => {
        const childPath = normalizePath(c.path);
        const childActive =
          childPath && (currentPath === childPath || currentPath.startsWith(`${childPath}/`));
        const childRelatedMatch = c.relatedPaths?.some((pattern) => {
          const normalizedPattern = normalizePath(pattern);
          return (
            normalizedPattern &&
            (currentPath === normalizedPattern || currentPath.startsWith(`${normalizedPattern}/`))
          );
        });
        return childActive || childRelatedMatch;
      });
      if (child) {
        return child.label;
      }
    }
  }
  return null;
};

/**
 * Build breadcrumbs from current path
 * @param {string} path - Current route path
 * @returns {Array} Breadcrumb items
 */
export const buildBreadcrumbs = (path) => {
  const breadcrumbs = [{ label: 'Home', path: '/home' }];

  const currentItem = getNavItemByPath(path);
  if (!currentItem) {
    return breadcrumbs;
  }

  // Find parent if exists
  for (const item of NAV_CONFIG) {
    if (item.children) {
      const child = item.children.find((c) => c.path === path);
      if (child) {
        breadcrumbs.push({ label: item.label, path: null });
        breadcrumbs.push({ label: child.label, path: child.path });
        return breadcrumbs;
      }
    }
  }

  // No parent, add current item
  if (path !== '/home') {
    breadcrumbs.push({ label: currentItem.label, path: currentItem.path });
  }

  return breadcrumbs;
};

export default NAV_CONFIG;
